import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandle } from './DragHandle';
import { ItemActionsMenu } from './ItemActionsMenu';
import type { ChecklistItem } from '../types';

type Props = {
  item: ChecklistItem;
  onEdit: (item: ChecklistItem) => void;
  onDelete: (id: string) => Promise<void>;
  disabled?: boolean;
  isEditing?: boolean;
};

export const SortableChecklistItem = ({
  item,
  onEdit,
  onDelete,
  disabled,
  isEditing = false
}: Props) => {
  const theme = useTheme();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const handleCardClick = (event: React.MouseEvent) => {
    if (event.target !== event.currentTarget) return;
    if (isDragging) return;
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      className="card-container"
      sx={{
        backgroundColor: isEditing ? 'action.selected' : 'background.paper',
        border: '1px solid',
        borderColor: isEditing
          ? 'primary.main'
          : isDragging
            ? 'divider'
            : isOver
              ? 'primary.light'
              : 'divider',
        borderRadius: '6px',
        padding: { xs: '8px 12px', sm: '12px 16px' },
        marginBottom: '4px',
        transition: isDragging
          ? 'none'
          : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: isDragging ? 'grabbing' : 'default',
        position: 'relative',
        transform: isDragging ? 'scale(1.01)' : 'scale(1)',
        opacity: isDragging ? 0.9 : 1,
        boxShadow: isDragging
          ? theme.shadows[8]
          : isOver
            ? theme.shadows[4]
            : theme.shadows[1],

        '&:hover':
          !isDragging && !disabled
            ? {
                borderColor: 'primary.light',
                boxShadow: theme.shadows[3],
                transform: 'translateY(-1px)',
                backgroundColor: 'action.hover'
              }
            : {}
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={{ xs: 1, sm: 2 }}
        flexDirection="row"
      >
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          isDragging={isDragging}
        />

        <Box
          sx={{
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            borderRadius: '6px',
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: { xs: '12px', sm: '14px' },
            fontWeight: 500,
            color: 'text.secondary',
            marginRight: { xs: '8px', sm: '16px' },
            flexShrink: 0,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {item.seq}
        </Box>

        <Typography
          component="span"
          sx={{
            flex: 1,
            lineHeight: 1.5,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            color: 'text.primary',
            fontSize: { xs: '14px', sm: '15px' },
            fontWeight: 400,
            display: 'block',
            minHeight: 20,
            maxWidth: '100%'
          }}
        >
          {item.text}
        </Typography>

        <ItemActionsMenu
          item={item}
          onEdit={onEdit}
          onDelete={onDelete}
          disabled={disabled || isDragging}
        />
      </Box>

      {isOver && (
        <Box
          sx={{
            position: 'absolute',
            top: -1,
            left: -1,
            right: -1,
            bottom: -1,
            borderRadius: '8px',
            border: '2px dashed',
            borderColor: 'primary.light',
            pointerEvents: 'none',
            zIndex: -1
          }}
        />
      )}
    </Box>
  );
};
